import { memo, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage, LANGUAGE_CONFIG } from '../hooks/useLanguage';
import { translations } from '../translations';
import { SectionHeader, GradientText, Card } from './ui';
import { ArrowRightIcon } from './icons';
import { cn } from '../lib/utils';
import CardPreviewContent, { colorGradient } from './CardPreviewContent';
import { useSSRListingPages, type SSRListingPage } from '../lib/ssr-data';

interface ArticlePage {
  id: number;
  slug: string;
  pageType: string;
  layout: string;
  previewColor?: string | undefined;
  sortOrder: number;
  title: string;
  content?: string;
  metaDescription?: string | undefined;
  previewContent?: string | undefined;
  previewContentHtml?: string | undefined;
  showInListing?: boolean;
  showInMenu?: boolean;
}

function fromSSR(items: SSRListingPage[]): ArticlePage[] {
  return items.map(p => ({
    ...p,
    previewColor: p.previewColor ?? undefined,
    metaDescription: p.metaDescription ?? undefined,
    previewContent: p.previewContent ?? undefined,
    previewContentHtml: p.previewContentHtml ?? undefined,
  }));
}

const DEFAULT_COLOR = '#4f6cff';

function extractExcerpt(content: string, maxLength = 150): string {
  const stripped = content
    .replace(/#{1,6}\s/g, '')
    .replace(/[*_~`>]/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    .replace(/\n+/g, ' ')
    .trim();
  if (stripped.length <= maxLength) return stripped;
  return stripped.slice(0, maxLength).replace(/\s\S*$/, '') + '…';
}

const Articles = memo(function Articles() {
  const { lang, localizedPath } = useLanguage();
  const isRTL = LANGUAGE_CONFIG[lang].isRTL;
  const ssrPages = useSSRListingPages();
  const validSSR = ssrPages?.filter(p => p.pageType === 'article');
  const ssrConsumed = useRef(!!validSSR?.length);
  const [pages, setPages] = useState<ArticlePage[]>(validSSR?.length ? fromSSR(validSSR) : []);
  const [loading, setLoading] = useState(!validSSR?.length);

  const t = translations[lang].articlesPage;

  useEffect(() => {
    if (ssrConsumed.current) {
      ssrConsumed.current = false;
      return;
    }
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/pages/public/by-type/article/${lang}`);
        if (response.ok) {
          const data = await response.json();
          setPages(data.pages ?? []);
        }
      } catch (err) {
        console.error('Failed to fetch articles:', err);
      } finally {
        setLoading(false);
      }
    };
    void fetchArticles();
  }, [lang]);

  const listingPages = pages.filter(p => p.showInListing);

  return (
    <div className="min-h-screen bg-slate-50 pt-32 pb-20">
      <div className="max-w-6xl mx-auto px-6">
        <SectionHeader
          badge={t.badge}
          title={
            <GradientText gradient="from-indigo-500 to-purple-500">{t.title}</GradientText>
          }
          subtitle={t.subtitle}
          theme="light"
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : listingPages.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-500 text-lg">{t.noArticles}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {listingPages.map((page) => {
              const color = page.previewColor || DEFAULT_COLOR;
              return (
                <Card
                  key={page.id}
                  variant="solid"
                  padding="none"
                  className="group overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="h-2" style={{ background: colorGradient(color) }} />

                  <div className="p-6">
                    {page.previewContentHtml ? (
                      <CardPreviewContent html={page.previewContentHtml} />
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-slate-900 mb-3 line-clamp-2">
                          {page.title}
                        </h3>
                        <p className="text-slate-600 text-sm mb-3 line-clamp-3">
                          {page.metaDescription || extractExcerpt(page.content || '')}
                        </p>
                      </>
                    )}

                    <Link
                      to={localizedPath(`/articles/${page.slug}`)}
                      className="inline-flex items-center gap-2 text-indigo-600 font-medium hover:text-indigo-800 transition-colors group/link mt-2"
                    >
                      <span>{t.readMore}</span>
                      <ArrowRightIcon
                        className={cn(
                          'w-4 h-4 transition-transform group-hover/link:translate-x-1',
                          isRTL && 'rotate-180 group-hover/link:-translate-x-1'
                        )}
                      />
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-16 text-center">
          <p className="text-slate-500 mb-4">{t.ctaText}</p>
          <Link
            to={localizedPath('/contact')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-full hover:bg-indigo-700 transition-colors"
          >
            {t.ctaButton}
          </Link>
        </div>
      </div>
    </div>
  );
});

export default Articles;
